import { CountryCode } from '@qred/shared-enums';
import moment from 'moment';
import { GlobalErrorType, OTPErrorType } from '~/enums';
import { logToSentry } from '~/helpers/loggers.helper';
import { getCookieValue } from '~/helpers/utils';
import {
  apiGetAdditionalSignerCardApplication,
  apiGetAdditionalSignerLoanApplication,
  apiPostInitiateOTP,
  apiPostRegenerateOTP,
  apiPostSignerCardOfferData,
  apiPostSignerConfirmation,
  apiPostVerifyOTP,
} from '~/services/api';
import { AppDispatch, GetStateFunc } from '..';
import {
  setAdditionalSignerCardApplicationData,
  setAdditionalSignerLoanApplicationData,
  setApiStatus,
  setOTP,
  setOTPError,
  setOTPExpiryTimestamp,
  setTimerCompleted,
} from '../slices/additionalSigner.slice';
import { toggleGlobalErrorOn } from '../slices/globalError.slice';
import { ApiStatus } from '../types/sharedTypes';

export const initiateOTP = (market: CountryCode) => (dispatch: AppDispatch) => {
  const productType = getCookieValue('additionalSignerProductType');
  const signerId = getCookieValue('additionalSignerId');
  const hashCode = getCookieValue('additionalSignerHashCode');

  dispatch(setApiStatus({ initiateOTP: ApiStatus.Started }));
  apiPostInitiateOTP(market, signerId, hashCode, productType)
    .then((response) => {
      if (response.data.otpExpiryTimestamp) {
        dispatch(
          setTimerCompleted(
            moment(response.data.otpExpiryTimestamp).isBefore(Date.now())
          )
        );
        dispatch(setOTPExpiryTimestamp(response.data.otpExpiryTimestamp));
        dispatch(setApiStatus({ initiateOTP: ApiStatus.Completed }));
      } else {
        dispatch(setApiStatus({ initiateOTP: ApiStatus.Failed }));
      }
    })
    .catch((err) => {
      const errorMessageIsKnown = Object.values(OTPErrorType).includes(
        err?.response?.data?.message
      );
      dispatch(
        setOTPError(
          errorMessageIsKnown ? err.response.data.message : OTPErrorType.UNKNOWN
        )
      );
      logToSentry(err, 'apiInitiateOTP', {});
      dispatch(setApiStatus({ initiateOTP: ApiStatus.Failed }));
    });
};

export const regenerateOTP = (market: CountryCode) => (
  dispatch: AppDispatch
) => {
  const signerId = getCookieValue('additionalSignerId');
  const hashCode = getCookieValue('additionalSignerHashCode');
  const productType = getCookieValue('additionalSignerProductType');

  dispatch(setOTPError(null));
  dispatch(setOTP(''));
  dispatch(
    setApiStatus({
      verifyOTP: ApiStatus.Idle,
      regenerateOTP: ApiStatus.Started,
    })
  );
  apiPostRegenerateOTP(market, signerId, hashCode, productType)
    .then((response) => {
      if (response.data.otpExpiryTimestamp) {
        dispatch(setOTPExpiryTimestamp(response.data.otpExpiryTimestamp));
        dispatch(setApiStatus({ regenerateOTP: ApiStatus.Completed }));
        dispatch(setTimerCompleted(false));
      } else {
        dispatch(setApiStatus({ regenerateOTP: ApiStatus.Failed }));
      }
    })
    .catch((err) => {
      const errorMessageIsKnown = Object.values(OTPErrorType).includes(
        err?.response?.data?.message
      );
      dispatch(
        setOTPError(
          errorMessageIsKnown ? err.response.data.message : OTPErrorType.UNKNOWN
        )
      );

      logToSentry(err, 'apiPostRegenerateOTP', {});
      dispatch(setApiStatus({ regenerateOTP: ApiStatus.Failed }));
    });
};

export const verifyOTP = (market: CountryCode, otp: string) => (
  dispatch: AppDispatch
) => {
  const signerId = getCookieValue('additionalSignerId');
  const hashCode = getCookieValue('additionalSignerHashCode');
  const productType = getCookieValue('additionalSignerProductType');

  dispatch(setApiStatus({ verifyOTP: ApiStatus.Started }));
  apiPostVerifyOTP(market, otp, signerId, hashCode, productType)
    .then((response) => {
      if (response.data) {
        if (response.data.accessToken) {
          document.cookie = `additionalSignerAccessToken=${response.data.accessToken}; Path=/; Max-Age=86400`;
        }

        dispatch(setApiStatus({ verifyOTP: ApiStatus.Completed }));
      } else {
        dispatch(setApiStatus({ verifyOTP: ApiStatus.Failed }));
      }
    })
    .catch((err) => {
      const errorMessageIsKnown = Object.values(OTPErrorType).includes(
        err?.response?.data?.message
      );
      dispatch(
        setOTPError(
          errorMessageIsKnown ? err.response.data.message : OTPErrorType.UNKNOWN
        )
      );
      logToSentry(err, 'apiVerifyOTP', {});
      dispatch(setApiStatus({ verifyOTP: ApiStatus.Failed }));
    });
};

export const confirmContractAction = () => (dispatch: AppDispatch) => {
  dispatch(
    setApiStatus({ postAdditionalSignerLoanApplication: ApiStatus.Started })
  );
  apiPostSignerConfirmation()
    .then((response) => {
      if (response.data) {
        window.location.assign(response.data.signURL);
      } else {
        throw new Error(`Response object has no data.`);
      }
    })
    .catch((err) => {
      dispatch(toggleGlobalErrorOn(GlobalErrorType.API_FAILURE));
      dispatch(
        setApiStatus({ postAdditionalSignerLoanApplication: ApiStatus.Failed })
      );
      logToSentry(err, 'apiPostSignerConfirmation');
    });
};

export const fetchAdditionalSignerLoanApplication = (market: CountryCode) => (
  dispatch: AppDispatch
) => {
  const signerId = getCookieValue('additionalSignerId');

  dispatch(
    setApiStatus({ fetchAdditionalSignerLoanApplication: ApiStatus.Started })
  );
  apiGetAdditionalSignerLoanApplication(market, signerId)
    .then((response) => {
      if (response.data) {
        dispatch(setAdditionalSignerLoanApplicationData(response.data));
        dispatch(
          setApiStatus({
            fetchAdditionalSignerLoanApplication: ApiStatus.Completed,
          })
        );
      } else {
        dispatch(
          setApiStatus({
            fetchAdditionalSignerLoanApplication: ApiStatus.Failed,
          })
        );
      }
    })
    .catch((err) => {
      logToSentry(err, 'apiGetAdditionalSignerLoanApplication', {});
      dispatch(
        setApiStatus({ fetchAdditionalSignerLoanApplication: ApiStatus.Failed })
      );
    });
};

export const fetchAdditionalSignerCardApplication = (
  market: CountryCode | null
) => (dispatch: AppDispatch) => {
  const signerId = getCookieValue('additionalSignerId');

  dispatch(
    setApiStatus({ fetchAdditionalSignerCardApplication: ApiStatus.Started })
  );
  apiGetAdditionalSignerCardApplication(market, signerId)
    .then((response) => {
      if (response.data) {
        dispatch(setAdditionalSignerCardApplicationData(response.data));
        dispatch(
          setApiStatus({
            fetchAdditionalSignerCardApplication: ApiStatus.Completed,
          })
        );
      } else {
        dispatch(
          setApiStatus({
            fetchAdditionalSignerCardApplication: ApiStatus.Failed,
          })
        );
      }
    })
    .catch((err) => {
      logToSentry(err, 'apiGetAdditionalSignerLoanApplication', {});
      dispatch(
        setApiStatus({ fetchAdditionalSignerCardApplication: ApiStatus.Failed })
      );
    });
};

export const sendSignersCardOfferData = () => (
  dispatch: AppDispatch,
  getState: GetStateFunc
) => {
  const {
    additionalSigner: { additionalSignerCardApplicationOffer: signerData },
    intl: { market },
  } = getState();
  dispatch(
    setApiStatus({ postAdditionalSignerCardApplication: ApiStatus.Started })
  );
  apiPostSignerCardOfferData(signerData, market)
    .then((res: any) => {
      const { data } = res;
      if (data?.message) {
        throw new Error(data.message);
      }
      if (data) {
        window.location.assign(data.signatureUrl);
      }
    })
    .catch((err: any) => {
      logToSentry(err, 'apiPostSignerCardOfferData');
      dispatch(
        setApiStatus({ postAdditionalSignerCardApplication: ApiStatus.Failed })
      );
      dispatch(toggleGlobalErrorOn(GlobalErrorType.API_FAILURE));
    });
};
